import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  Settings2,
  RotateCcw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { ApprovalWorkflow } from '@/types/workflow-assignment';
import * as workflowAssignmentService from '@/services/workflowAssignmentService';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';

interface WorkflowSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentWorkflowId?: string | null;
  employeeName?: string;
  onSelect: (workflowId: string | null, reason?: string) => Promise<boolean>;
}

export const WorkflowSelectionModal: React.FC<WorkflowSelectionModalProps> = ({
  open,
  onOpenChange,
  currentWorkflowId,
  employeeName,
  onSelect,
}) => {
  const { mode } = useScreenDataMode('team-members');
  const [workflows, setWorkflows] = useState<ApprovalWorkflow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [showNoApprovalConfirmation, setShowNoApprovalConfirmation] = useState(false);
  const [noApprovalReason, setNoApprovalReason] = useState('');
  const [understoodNoApproval, setUnderstoodNoApproval] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      loadWorkflows();
      setSelectedWorkflowId(currentWorkflowId || 'DEFAULT');
      setReason('');
    }
  }, [open, currentWorkflowId]);

  const loadWorkflows = async () => {
    setIsLoading(true);
    try {
      // In demo mode, return mock workflows
      if (mode === 'demo') {
        await new Promise(resolve => setTimeout(resolve, 500));
        const mockWorkflows: ApprovalWorkflow[] = [
          {
            id: 'default-workflow',
            name: 'Standard Approval Workflow',
            description: 'Standard two-stage approval process',
            isActive: true,
            isDefault: true,
            createdAt: new Date('2024-01-01'),
            createdBy: 'system',
            version: 1,
            stages: [],
            totalStages: 2,
            totalApprovers: 3,
            isInUse: true,
          },
          {
            id: 'executive-workflow',
            name: 'Executive Approval Workflow',
            description: 'Multi-stage approval for executives',
            isActive: true,
            isDefault: false,
            createdAt: new Date('2024-01-15'),
            createdBy: 'admin',
            version: 1,
            stages: [],
            totalStages: 4,
            totalApprovers: 6,
            isInUse: false,
          },
          {
            id: 'fast-track-workflow',
            name: 'Fast Track Approval',
            description: 'Single-stage approval for trusted employees',
            isActive: true,
            isDefault: false,
            createdAt: new Date('2024-02-01'),
            createdBy: 'admin',
            version: 1,
            stages: [],
            totalStages: 1,
            totalApprovers: 1,
            isInUse: false,
          },
        ];
        setWorkflows(mockWorkflows);
      } else {
        // In live mode, fetch from API
        const data = await workflowAssignmentService.getAllWorkflows();
        setWorkflows(data.filter((w) => w.isActive));
      }
    } catch (err) {
      console.error('Error loading workflows:', err);
      // Only show toast error in live mode
      if (mode === 'live') {
        toast.error('Failed to load workflows');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = async () => {
    // Special handling for NO_APPROVAL
    if (selectedWorkflowId === 'NO_APPROVAL') {
      setNoApprovalReason(reason);
      setShowNoApprovalConfirmation(true);
      return;
    }

    // Regular workflow assignment
    await handleConfirmedSelect();
  };

  const handleConfirmedSelect = async () => {
    setIsSubmitting(true);
    try {
      const workflowId = selectedWorkflowId === 'DEFAULT' ? null : selectedWorkflowId;
      const finalReason = selectedWorkflowId === 'NO_APPROVAL' ? noApprovalReason : reason;
      
      const success = await onSelect(workflowId, finalReason);
      
      if (success) {
        onOpenChange(false);
        setShowNoApprovalConfirmation(false);
        setNoApprovalReason('');
        setUnderstoodNoApproval(false);
      }
    } catch (err) {
      console.error('Error assigning workflow:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getWorkflowOption = (workflow: ApprovalWorkflow | 'DEFAULT' | 'NO_APPROVAL') => {
    if (workflow === 'DEFAULT') {
      const defaultWorkflow = workflows.find((w) => w.isDefault);
      return {
        id: 'DEFAULT',
        name: 'Use Organization Default (Recommended)',
        description: defaultWorkflow
          ? `${defaultWorkflow.name} • ${defaultWorkflow.totalStages} stages • ${defaultWorkflow.totalApprovers} approvers`
          : 'Standard approval workflow',
        icon: '🔄',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50 border-blue-200',
      };
    }

    if (workflow === 'NO_APPROVAL') {
      return {
        id: 'NO_APPROVAL',
        name: 'No Approval Required',
        description: 'Timesheets auto-approve upon submission • Use for contractors or trusted employees',
        icon: '✅',
        color: 'text-green-600',
        bgColor: 'bg-green-50 border-green-200',
        warning: true,
      };
    }

    return {
      id: workflow.id,
      name: workflow.name,
      description: `${workflow.description || ''} • ${workflow.totalStages} stages • ${workflow.totalApprovers} approvers`,
      icon: '⚙️',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50 border-purple-200',
    };
  };

  const allOptions = [
    'DEFAULT' as const,
    ...workflows.filter((w) => !w.isDefault),
    'NO_APPROVAL' as const,
  ];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign Approval Workflow</DialogTitle>
            <DialogDescription>
              Select the approval workflow for{' '}
              {employeeName ? `${employeeName}'s` : 'this employee\'s'} timesheets
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : (
            <div className="space-y-4">
              <RadioGroup
                value={selectedWorkflowId || 'DEFAULT'}
                onValueChange={setSelectedWorkflowId}
                className="space-y-3"
              >
                {allOptions.map((option) => {
                  const workflowOption = getWorkflowOption(option);
                  const isSelected = selectedWorkflowId === workflowOption.id;

                  return (
                    <div
                      key={workflowOption.id}
                      className={cn(
                        'border rounded-lg p-4 cursor-pointer transition-all',
                        isSelected
                          ? `${workflowOption.bgColor} border-2`
                          : 'border-gray-200 hover:border-gray-300',
                        workflowOption.warning && 'border-amber-300'
                      )}
                      onClick={() => setSelectedWorkflowId(workflowOption.id)}
                    >
                      <div className="flex items-start gap-3">
                        <RadioGroupItem
                          value={workflowOption.id}
                          id={workflowOption.id}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor={workflowOption.id}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <span className="text-xl">{workflowOption.icon}</span>
                            <span className="font-medium">{workflowOption.name}</span>
                            {workflowOption.warning && (
                              <AlertTriangle className="h-4 w-4 text-amber-600" />
                            )}
                          </Label>
                          <p className="text-sm text-gray-600 mt-1">
                            {workflowOption.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </RadioGroup>

              {/* Reason Input */}
              <div className="space-y-2">
                <Label htmlFor="reason">
                  Reason for Change{' '}
                  <span className="text-gray-500 font-normal">(Optional)</span>
                </Label>
                <Textarea
                  id="reason"
                  placeholder='e.g., "Promoted to senior role" or "Contractor with pre-approved hours"'
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                />
              </div>

              {/* Info Alert */}
              {selectedWorkflowId === 'NO_APPROVAL' && (
                <Alert className="border-amber-500 bg-amber-500/10">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700 text-sm">
                    This will bypass all approval controls. Use only for highly trusted
                    employees or contractors with pre-approved hours.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSelect}
              disabled={isLoading || isSubmitting || !selectedWorkflowId}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Assign Workflow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* No Approval Confirmation Dialog */}
      <AlertDialog
        open={showNoApprovalConfirmation}
        onOpenChange={setShowNoApprovalConfirmation}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Confirm No Approval Assignment
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <div>
                You are about to enable automatic approval for:
                <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">
                    👤 {employeeName || 'This employee'}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <p className="font-medium text-gray-900">This means:</p>
                <ul className="space-y-1 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    Timesheets will automatically approve upon submission
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    No manual review will be required
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    Status will change directly to "Fully Approved"
                  </li>
                </ul>
              </div>

              <Alert className="border-amber-500 bg-amber-500/10">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700 text-sm">
                  <p className="font-medium mb-1">Important Considerations:</p>
                  <ul className="space-y-1 text-xs">
                    <li>• This bypasses all approval controls</li>
                    <li>• Use only for highly trusted employees</li>
                    <li>• Audit logs will still track all submissions</li>
                    <li>• You can change this setting at any time</li>
                  </ul>
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label htmlFor="no-approval-reason">
                  Reason for No Approval <span className="text-red-600">*</span>
                </Label>
                <Textarea
                  id="no-approval-reason"
                  placeholder='e.g., "Contractor with pre-approved hours"'
                  value={noApprovalReason}
                  onChange={(e) => setNoApprovalReason(e.target.value)}
                  rows={2}
                  required
                />
              </div>

              <div className="flex items-start gap-2">
                <Checkbox
                  id="understand"
                  checked={understoodNoApproval}
                  onCheckedChange={(checked) =>
                    setUnderstoodNoApproval(checked as boolean)
                  }
                />
                <Label
                  htmlFor="understand"
                  className="text-sm font-normal cursor-pointer"
                >
                  I understand the implications of this change
                </Label>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowNoApprovalConfirmation(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmedSelect}
              disabled={!understoodNoApproval || !noApprovalReason.trim() || isSubmitting}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
